import React, { useRef } from 'react';
import Div from '../Element/Div/Div';
import Icon from '../Icon/Icon';
import styles from './OverflowMenu.module.scss';
import Flex from '../Flex/Flex';
import { CommonProps } from '../Element/Element';
import OverflowMenuItem from './OverflowMenuItem/OverflowMenuItem';
import useOverflowMenu from './useOverflowMenu';

export type OverflowMenuProps = {
  /** Function triggered on click */
  onClick: () => void;
  /** Is menu open */
  open: boolean;
  /** Menu position, either left or right */
  position?: 'right' | 'left';
} & CommonProps<'div'>;

export const OverflowMenu: React.FunctionComponent<OverflowMenuProps> & {
  Item: typeof OverflowMenuItem;
} = ({
  onClick,
  open,
  position = 'right',
  children,
  backgroundColor = 'white', // Set backgroundColor default to white if not provided
  ...props
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onClick) {
      onClick();
      event.preventDefault();
    }
  };

  useOverflowMenu(menuRef, onClick, open);

  return (
    <Div {...props} className={styles.overflowMenu} refElement={menuRef}>
      <Flex
        className={[styles.overflowMenu_iconWrapper, 'overflow-menu-test']}
        centered
        borderRadius={{ size: 'rounded' }}
        clickableAppearance
        onKeyDown={onKeyDown}
        tabIndex={0}
        onClick={onClick}
        backgroundColor={open ? 'gray200' : backgroundColor}
      >
        <Icon name="more-vertical" size="md" />
      </Flex>

      {open && (
        <Div
          className={[
            styles.overflowMenu_content,
            { [styles.overflowMenu_content__left]: position === 'left' },
            { [styles.overflowMenu_content__right]: position === 'right' },
          ]}
          boxShadow="sm"
        >
          {children}
        </Div>
      )}
    </Div>
  );
};

OverflowMenu.Item = OverflowMenuItem;

export default OverflowMenu;
